import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { logError } from '../utils/logger';
import { getPlanLabel, getPlanMaxPickups, getPlanMaxPackages } from '../utils/planHelpers';
import PickupDetailsStep from './schedule/PickupDetailsStep';
import PackagesStep from './schedule/PackagesStep';
import ReviewStep from './schedule/ReviewStep';

const SchedulePickup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [savedAddress, setSavedAddress] = useState(null);  // From last workplace booking
  const [savedCartId, setSavedCartId] = useState(null);    // Abandoned-cart resume id
  const [duplicateWarning, setDuplicateWarning] = useState(null); // {recent_booking, override}
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationType, setLocationType] = useState('office'); // 'office' or 'workplace'
  const [workplaceAddress, setWorkplaceAddress] = useState({
    street: '',
    apartment: '',
    city: 'Scottsdale',
    state: 'AZ',
    zipCode: '',
    pickupNotes: ''
  });
  const [returnDate, setReturnDate] = useState('');
  const [numberOfPackages, setNumberOfPackages] = useState(1);
  const [rushDropoff, setRushDropoff] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [packageDetails, setPackageDetails] = useState({});
  const [businessName, setBusinessName] = useState('');
  const [errors, setErrors] = useState({});

  // Booking-wide acknowledgment checkboxes (apply to all packages)
  const [liabilityWaiverAccepted, setLiabilityWaiverAccepted] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  
  // New operational features
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [serviceAreaZips, setServiceAreaZips] = useState(['85254', '85032']);
  const [zipCodeValid, setZipCodeValid] = useState(true);
  
  // Subscription state
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [isSubscription, setIsSubscription] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  
  // Packaging add-ons (separate from packages)
  const [packagingAddons, setPackagingAddons] = useState({
    small: 0,
    medium: 0,
    large: 0
  });
  
  // Oversized package option
  const [hasOversizedPackage, setHasOversizedPackage] = useState(false);

  // 3-step wizard: 1 = Pickup Details, 2 = Packages, 3 = Review
  const [currentStep, setCurrentStep] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const packageSizes = [
    { 
      value: 'small', 
      label: 'Small Box', 
      maxSize: 'Up to 12×9×6 inches',
      description: 'Small return items like clothing, accessories, or small electronics',
      weight: '0-5 lbs',
      price: 3.00
    },
    { 
      value: 'medium', 
      label: 'Medium Box', 
      maxSize: 'Up to 18×14×10 inches',
      description: 'Items like shoe boxes, handbags, or small appliances',
      weight: '5-15 lbs',
      price: 4.00
    },
    { 
      value: 'large', 
      label: 'Large Box', 
      maxSize: 'Up to 24×18×16 inches',
      description: 'Larger retail returns like electronics boxes or multiple clothing items',
      weight: '15-30 lbs',
      price: 5.00
    }
  ];

  const locations = [
    {
      id: 'spaces-quarter',
      name: '15169 N Scottsdale Rd Suite 205 (SPACES Quarter)',
      address: '15169 N Scottsdale Rd Suite 205, Scottsdale, AZ 85254'
    },
    {
      id: 'scottsdale-quarter-business',
      name: 'Scottsdale Quarter Businesses',
      address: '15169 N Scottsdale Rd, Scottsdale, AZ 85254',
      requiresBusinessName: true
    },
    {
      id: 'promenade',
      name: 'Scottsdale Promenade Corporate Center (Reception)',
      address: '16427 N Scottsdale Rd Suite 410, Scottsdale, AZ 85254'
    },
    {
      id: 'raintree',
      name: 'Raintree Corporate Center (Reception)',
      address: '15333 N Pima Rd Suite 305, Scottsdale, AZ 85260'
    },
    {
      id: 'kierland',
      name: 'Kierland Corporate Center (Reception)',
      address: '7047 E Greenway Pkwy Suite 250, Scottsdale, AZ 85254'
    }
  ];

  const carriers = [
    { id: 'ups', name: 'UPS – Non‑Amazon Return' },
    { id: 'fedex', name: 'FedEx – Non‑Amazon Return' },
    { id: 'amazon-ups-qr', name: 'Amazon – UPS QR Code (UPS Store locations only)' },
    { id: 'amazon-ups-label', name: 'Amazon – UPS Shipping Label (Accepted at most UPS drop‑off locations)' },
    { id: 'amazon-fedex-qr', name: 'Amazon – FedEx QR Code (FedEx Office locations only)' },
    { id: 'amazon-fedex-label', name: 'Amazon – FedEx Shipping Label (Accepted at most FedEx drop‑off locations)' },
    { id: 'amazon-wholefoods', name: 'Amazon – Whole Foods Market (QR Code / Eligible Items Only)' },
    { id: 'amazon-kohls', name: "Amazon – Kohl's (QR Code / Eligible Items Only)" },
    { id: 'amazon-staples', name: 'Amazon – Staples (QR Code / Participating Locations Only)' },
  ];

  const fetchSubscriptionUsage = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${email}/subscription-usage`);
      if (response.ok) {
        const data = await response.json();
        setSubscriptionUsage(data);
      }
    } catch (error) {
      logError('Failed to fetch subscription usage:', error);
      // Non-critical - subscription usage is not critical for booking flow
    } finally {
      setLoadingSubscription(false);
    }
  }, [API_URL]);

  const fetchBlackoutDates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blackout-dates`);
      if (response.ok) {
        const data = await response.json();
        setBlackoutDates(data.blackout_dates || []);
      }
    } catch (error) {
      logError('Failed to fetch blackout dates:', error);
      // Non-critical - continue with empty array
    }
  }, [API_URL]);

  const fetchServiceArea = useCallback(async () => {
    try {
      // SECURITY FIX: Use public endpoint instead of hardcoded admin token
      const response = await fetch(`${API_URL}/api/admin/service-area/zip-codes`);
      if (response.ok) {
        const data = await response.json();
        setServiceAreaZips(data.service_area_zip_codes || ['85254', '85032']);
      }
    } catch (error) {
      logError('Failed to fetch service area:', error);
      // Non-critical - continue with default zips
    }
  }, [API_URL]);

  const validateZipCode = async (zipCode) => {
    if (!zipCode || zipCode.length !== 5) {
      setZipCodeValid(false);
      return false;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/service-area/validate?zip_code=${zipCode}`);
      if (response.ok) {
        const data = await response.json();
        setZipCodeValid(data.valid);
        if (!data.valid) {
          setErrors(prev => ({
            ...prev,
            zipCode: data.message
          }));
        } else {
          setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.zipCode;
            return newErrors;
          });
        }
        return data.valid;
      }
    } catch (error) {
      logError('Failed to validate zip code:', error);
    }
    return false;
  };

  const checkIfSameDay = (selectedDate) => {
    const today = new Date().toISOString().split('T')[0];
    const isSame = selectedDate === today;
    setIsSameDay(isSame);
    return isSame;
  };


  useEffect(() => {
    // Fetch operational data
    fetchBlackoutDates();
    fetchServiceArea();
    
    // Check if customer info exists
    const customerInfo = sessionStorage.getItem('customerInfo'); // TODO: Security - migrate to httpOnly cookies
    if (!customerInfo) {
      // Check if user is logged in (coming from dashboard)
      const isLoggedIn = sessionStorage.getItem('isLoggedIn');
      if (!isLoggedIn) {
        navigate('/signup');
      }
    } else {
      // Fetch subscription usage if user is subscribed
      const parsedInfo = JSON.parse(customerInfo);
      setIsSubscription(parsedInfo.isSubscription || false);
      
      if (parsedInfo.isSubscription && parsedInfo.email) {
        fetchSubscriptionUsage(parsedInfo.email);
      } else {
        setLoadingSubscription(false);
      }

      // Fetch last-used workplace address (for "Use saved address" button)
      if (parsedInfo.email) {
        fetch(`${API_URL}/api/users/${encodeURIComponent(parsedInfo.email)}/last-workplace-address`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data?.has_saved_address && data?.address) {
              setSavedAddress({
                street: data.address.street || '',
                apartment: data.address.apartment || '',
                city: data.address.city || 'Scottsdale',
                state: data.address.state || 'AZ',
                zipCode: data.zip_code || data.address.zipCode || '',
                pickupNotes: data.pickup_notes || data.address.pickupNotes || '',
              });
            }
          })
          .catch((err) => logError('Saved address fetch failed', err));
      }
    }

    // Resume from abandoned cart (?resume=<cart_id>)
    const resumeId = searchParams.get('resume');
    if (resumeId) {
      fetch(`${API_URL}/api/abandoned-carts/${resumeId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (!cart || !cart.cart_data) return;
          setSavedCartId(cart.cart_id);
          const c = cart.cart_data;
          if (c.locationType) setLocationType(c.locationType);
          if (c.selectedLocation) setSelectedLocation(c.selectedLocation);
          if (c.workplaceAddress) setWorkplaceAddress(c.workplaceAddress);
          if (c.businessName) setBusinessName(c.businessName);
          if (c.returnDate) setReturnDate(c.returnDate);
          if (c.selectedTime) setSelectedTime(c.selectedTime);
        })
        .catch((err) => logError('Cart resume failed', err));
    }
  }, [navigate, fetchBlackoutDates, fetchServiceArea, fetchSubscriptionUsage, searchParams, API_URL]);

  const calculatePricing = () => {
    let total = 0;
    let breakdown = {
      baseTier: 0,
      additionalPickupFee: 0,
      additionalPackageFee: 0,
      packagingCost: 0,
      oversizedFee: 0,
      rushFee: 0,
      subscriptionSavings: 0
    };
    
    // SUBSCRIPTION PRICING LOGIC
    // Plans: Your Pal ($29.99) - 2 pickups, 10 pkg/month | My Pal ($39.99) - 3 pickups, 15 pkg/month | Best Pals ($49.99) - 4 pickups, 20 pkg/month
    // Packages ROLL OVER within the month (can use all monthly packages flexibly across pickups)
    // Cap: 10 packages max per pickup
    // Additional packages: $5 each | Additional pickups: $15 each
    if (isSubscription && subscriptionUsage) {
      const pickupsUsed = subscriptionUsage.pickups_used_this_month;
      const packagesUsed = subscriptionUsage.packages_used_this_month;
      
      // Determine user's plan limits based on their subscription
      let maxPickups = 2; // Default for Your Pal
      let maxPackagesPerMonth = 10;
      if (subscriptionUsage.plan_type === 'my-pal') {
        maxPickups = 3;
        maxPackagesPerMonth = 15;
      } else if (subscriptionUsage.plan_type === 'best-pals') {
        maxPickups = 4;
        maxPackagesPerMonth = 20;
      }
      
      const pickupsRemaining = Math.max(0, maxPickups - pickupsUsed);
      const packagesRemaining = Math.max(0, maxPackagesPerMonth - packagesUsed);
      
      // Check if within included pickups
      if (pickupsRemaining > 0) {
        // Within included pickups - pickup is covered by subscription
        breakdown.baseTier = 0;
        
        // Check if packages exceed monthly allowance
        const packagesOverMonthlyLimit = Math.max(0, (packagesUsed + numberOfPackages) - maxPackagesPerMonth);
        if (packagesOverMonthlyLimit > 0) {
          // Exceeds monthly package allowance - charge $5 per additional package
          breakdown.additionalPackageFee = packagesOverMonthlyLimit * 5;
          total += breakdown.additionalPackageFee;
        }
        
        // Calculate savings
        breakdown.subscriptionSavings = 19.99;
      } else {
        // No pickups remaining - charge $15 for additional pickup
        breakdown.additionalPickupFee = 15;
        total += 15;
        
        // Additional packages beyond monthly allowance still cost $5 each
        const packagesOverMonthlyLimit = Math.max(0, (packagesUsed + numberOfPackages) - maxPackagesPerMonth);
        if (packagesOverMonthlyLimit > 0) {
          breakdown.additionalPackageFee = packagesOverMonthlyLimit * 5;
          total += breakdown.additionalPackageFee;
        }
      }
      
    } else {
      // PAY-AS-YOU-GO PRICING (Let's Be Pals)
      // $19.99 for 1 pickup, up to 3 packages, NO rollover
      breakdown.baseTier = 19.99;
      total += 19.99;
      
      // Additional packages: $5 each (max 10 total per pickup)
      const additionalPackages = Math.max(0, numberOfPackages - 3);
      if (additionalPackages > 0) {
        breakdown.additionalPackageFee = additionalPackages * 5;
        total += breakdown.additionalPackageFee;
      }
    }
    
    // Packaging add-ons (calculate from individual package selections)
    let packagingCost = 0;
    let rushFeeTotal = 0;
    Object.values(packageDetails).forEach(pkg => {
      if (pkg.packaging === 'small') packagingCost += 3;
      else if (pkg.packaging === 'medium') packagingCost += 4;
      else if (pkg.packaging === 'large') packagingCost += 5;
      
      // Count rush delivery per package
      if (pkg.rushDelivery) rushFeeTotal += 10;
    });
    breakdown.packagingCost = packagingCost;
    total += breakdown.packagingCost;
    
    breakdown.rushFee = rushFeeTotal;
    total += breakdown.rushFee;
    
    // Oversized package fee
    if (hasOversizedPackage) {
      breakdown.oversizedFee = 8; // $8 per oversized package (flat fee)
      total += breakdown.oversizedFee;
    }
    
    return { total, breakdown };
  };

  const handleFileUpload = async (packageNumber, e) => {
    const file = e.target.files[0];
    if (file) {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => ({
          ...prev,
          [packageNumber]: {
            file: file,
            base64: reader.result,
            name: file.name
          }
        }));
        // Clear error for this package
        if (errors[`file${packageNumber}`]) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`file${packageNumber}`];
            return newErrors;
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePackageDetailChange = (packageNumber, field, value) => {
    setPackageDetails(prev => ({
      ...prev,
      [packageNumber]: {
        ...(prev[packageNumber] || {}),
        [field]: value
      }
    }));
    
    // Clear related errors
    if (errors[`${field}${packageNumber}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${field}${packageNumber}`];
        return newErrors;
      });
    }
  };

  const handlePackagingChange = (size, delta) => {
    setPackagingAddons(prev => ({
      ...prev,
      [size]: Math.max(0, prev[size] + delta)
    }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (locationType === 'office' && !selectedLocation) {
      newErrors.location = 'Please select a location';
    }
    if (locationType === 'workplace') {
      if (!workplaceAddress.street) newErrors.street = 'Office building address is required';
      if (!workplaceAddress.city) newErrors.city = 'City is required';
      if (!workplaceAddress.state) newErrors.state = 'State is required';
      if (!workplaceAddress.zipCode) newErrors.zipCode = 'Zip code is required';
      else if (workplaceAddress.zipCode.length !== 5) newErrors.zipCode = 'Zip code must be 5 digits';
      else if (!zipCodeValid) newErrors.zipCode = `We only service: ${serviceAreaZips.join(', ')}`;
      if (!workplaceAddress.pickupNotes || !workplaceAddress.pickupNotes.trim()) {
        newErrors.pickupNotes = 'Pickup instructions are required for workplace pickups';
      }
    }
    if (!returnDate) newErrors.returnDate = 'Please select a pickup date';
    if (blackoutDates.includes(returnDate)) newErrors.returnDate = '🚫 This date is unavailable';
    if (!selectedTime) newErrors.time = 'Please select a pickup window';
    return newErrors;
  };

  const handleNextStep = async () => {
    const stepErrors = validateStep1();
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) {
      // Save abandoned cart snapshot (fire-and-forget)
      try {
        const customerInfo = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');
        if (customerInfo.email) {
          const cartData = {
            locationType,
            selectedLocation,
            workplaceAddress,
            businessName,
            returnDate,
            selectedTime,
          };
          fetch(`${API_URL}/api/abandoned-carts/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: customerInfo.email,
              customer_info: customerInfo,
              cart_data: cartData,
            }),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => { if (data?.cart_id) setSavedCartId(data.cart_id); })
            .catch((err) => logError('Cart save failed', err));
        }
      } catch (err) {
        // Non-fatal: cart save shouldn't block step transition
        logError('Cart save threw', err);
      }
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Validates packages step (everything except final agreement checkbox)
  const validateStep2 = () => {
    const newErrors = {};
    if (numberOfPackages < 1) newErrors.packages = 'Must have at least 1 package';
    if (!liabilityWaiverAccepted) {
      newErrors.liabilityWaiver = 'Please acknowledge the Return Method Liability Waiver before continuing';
    }
    for (let i = 1; i <= numberOfPackages; i++) {
      const pkg = packageDetails[i] || {};
      const packageType = pkg.packageType || 'label';
      if (!uploadedFiles[i]) {
        newErrors[`file${i}`] = `${packageType === 'qr' ? 'QR code' : 'Label'} required for Package ${i}`;
      }
      if (!pkg.carrier) {
        newErrors[`carrier${i}`] = `Carrier required for Package ${i}`;
      }
      if (packageType === 'label') {
        if (!pkg.tracking_last6 || pkg.tracking_last6.trim() === '') {
          newErrors[`tracking${i}`] = `Last 6 digits of tracking number required for Package ${i}`;
        } else if (pkg.tracking_last6.length !== 6 || !/^\d{6}$/.test(pkg.tracking_last6)) {
          newErrors[`tracking${i}`] = `Must be exactly 6 digits for Package ${i}`;
        }
      }
      if (!pkg.notes || pkg.notes.trim() === '') {
        newErrors[`notes${i}`] = `Item description required for Package ${i}`;
      }
    }
    return newErrors;
  };

  const handleNextFromPackages = () => {
    const stepErrors = validateStep2();
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackStep = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    // Location type validation
    if (locationType === 'office' && !selectedLocation) {
      newErrors.location = 'Please select a location';
    }
    if (locationType === 'workplace') {
      if (!workplaceAddress.street) newErrors.street = 'Office building address is required';
      if (!workplaceAddress.city) newErrors.city = 'City is required';
      if (!workplaceAddress.state) newErrors.state = 'State is required';
      if (!workplaceAddress.zipCode) newErrors.zipCode = 'Zip code is required';
      else if (workplaceAddress.zipCode.length !== 5) newErrors.zipCode = 'Zip code must be 5 digits';
      else if (!zipCodeValid) newErrors.zipCode = `We only service: ${serviceAreaZips.join(', ')}`;
      if (!workplaceAddress.pickupNotes || !workplaceAddress.pickupNotes.trim()) {
        newErrors.pickupNotes = 'Pickup instructions are required for workplace pickups';
      }
    }
    
    if (!selectedTime) newErrors.time = 'Please select a pickup window';
    if (!returnDate) newErrors.returnDate = 'Please select a pickup date';
    if (blackoutDates.includes(returnDate)) newErrors.returnDate = '🚫 This date is unavailable';
    if (numberOfPackages < 1) newErrors.packages = 'Must have at least 1 package';

    // Booking-wide acknowledgment gates
    if (!liabilityWaiverAccepted) {
      newErrors.liabilityWaiver = 'Please acknowledge the Return Method Liability Waiver before continuing';
    }
    if (!agreementAccepted) {
      newErrors.agreement = "Please confirm you've read and agree to ReturnPal's Terms";
    }
    
    // Check that all packages have required fields
    for (let i = 1; i <= numberOfPackages; i++) {
      const pkg = packageDetails[i] || {};
      const packageType = pkg.packageType || 'label';
      
      // Check file upload
      if (!uploadedFiles[i]) {
        newErrors[`file${i}`] = `${packageType === 'qr' ? 'QR code' : 'Label'} required for Package ${i}`;
      }
      
      // Type-specific validations
      if (!pkg.carrier) {
        newErrors[`carrier${i}`] = `Carrier required for Package ${i}`;
      }
      
      if (packageType === 'label') {
        if (!pkg.tracking_last6 || pkg.tracking_last6.trim() === '') {
          newErrors[`tracking${i}`] = `Last 6 digits of tracking number required for Package ${i}`;
        } else if (pkg.tracking_last6.length !== 6 || !/^\d{6}$/.test(pkg.tracking_last6)) {
          newErrors[`tracking${i}`] = `Must be exactly 6 digits for Package ${i}`;
        }
      }
      
      // Item description required for all
      if (!pkg.notes || pkg.notes.trim() === '') {
        newErrors[`notes${i}`] = `Item description required for Package ${i}`;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Get customer info
      const customerInfo = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');
      
      // Build packages array with all details
      const packages = [];
      for (let i = 1; i <= numberOfPackages; i++) {
        const pkg = packageDetails[i] || {};
        const packageType = pkg.packageType || 'label';
        const uploadedFile = uploadedFiles[i];
        
        packages.push({
          packageNumber: i,
          package_type: packageType,
          carrier: pkg.carrier,
          tracking_last6: packageType === 'label' ? pkg.tracking_last6 : null,
          // For QR packages: store the actual uploaded image (base64). For label packages: null.
          qr_image: packageType === 'qr' ? (uploadedFile?.base64 || null) : null,
          hasLithiumBatteries: pkg.hasLithiumBatteries || false,
          isFragile: pkg.isFragile || false,
          // Backend Package model requires `item_description` (not `notes`)
          item_description: pkg.notes || '',
          labelFileName: uploadedFile?.name || '',
          label_url: uploadedFile?.base64 || null,
          packaging_size: pkg.packaging && pkg.packaging !== 'none' ? pkg.packaging : null,
          rush_delivery: pkg.rushDelivery || false
        });
      }
      
      const { total: totalCost, breakdown } = calculatePricing();
      const totalPackagingBoxes = packagingAddons.small + packagingAddons.medium + packagingAddons.large;
      
      // Build location data based on type
      const locationData = locationType === 'office' 
        ? {
            locationName: locations.find(loc => loc.id === selectedLocation)?.name || '',
            locationAddress: locations.find(loc => loc.id === selectedLocation)?.address || '',
            locationType: 'office'
          }
        : {
            locationName: 'Workplace Pickup',
            locationAddress: `${workplaceAddress.street}${workplaceAddress.apartment ? ', ' + workplaceAddress.apartment : ''}, ${workplaceAddress.city}, ${workplaceAddress.state} ${workplaceAddress.zipCode}`,
            locationType: 'workplace',
            pickupAddress: workplaceAddress,
            pickupNotes: workplaceAddress.pickupNotes,
            zipCode: workplaceAddress.zipCode
          };
      
      // Store booking data in sessionStorage (NO same-day surcharge)
      const bookingData = {
        ...locationData,
        customerEmail: customerInfo.email || '',
        pickupDate: new Date(returnDate).toLocaleDateString(),
        pickupWindow: selectedTime,
        carrier: packages[0]?.carrier || 'Multiple',
        numberOfPackages: numberOfPackages,
        labelsUploaded: Object.keys(uploadedFiles).length,
        packagingCount: totalPackagingBoxes,
        packagingCost: breakdown.packagingCost,
        packagingAddons: packagingAddons,
        hasOversizedPackage: hasOversizedPackage,
        rushDropoff: rushDropoff,
        additionalCost: totalCost - 23,
        totalCost: totalCost,
        pricingBreakdown: breakdown,
        packages: packages
      };
      
      // Store booking data in sessionStorage with error handling
      try {
        sessionStorage.setItem('bookingData', JSON.stringify(bookingData));
        if (savedCartId) {
          sessionStorage.setItem('resumedCartId', savedCartId);
        }
      } catch (error) {
        setErrors({ submit: 'Failed to save booking data. Please try again.' });
        setIsSubmitting(false);
        return;
      }
      
      // Duplicate-booking soft check (1-hour window)
      try {
        const dupRes = await fetch(
          `${API_URL}/api/bookings/check-duplicate?email=${encodeURIComponent(customerInfo.email || '')}`,
        );
        if (dupRes.ok) {
          const dup = await dupRes.json();
          if (dup.has_duplicate && !duplicateWarning) {
            // Show inline warning instead of immediate navigate; user clicks Continue Anyway
            setDuplicateWarning(dup.recent_booking || {});
            return;
          }
        }
      } catch (err) {
        // Non-fatal: dup check shouldn't block payment navigation
        logError('Duplicate-check failed', err);
      }
      
      // Navigate to payment page
      navigate('/payment');
    }
  };

  const { total: totalCost, breakdown } = calculatePricing();

  // Bundle all state, setters, helpers, and constants for child step components
  const stepCtx = {
    // Step 1
    selectedLocation, setSelectedLocation,
    locationType, setLocationType,
    workplaceAddress, setWorkplaceAddress,
    businessName, setBusinessName,
    returnDate, setReturnDate,
    selectedTime, setSelectedTime,
    locations, blackoutDates, serviceAreaZips,
    zipCodeValid, validateZipCode, checkIfSameDay,
    handleNextStep,
    savedAddress,
    // Step 2
    rushDropoff, setRushDropoff,
    numberOfPackages, setNumberOfPackages,
    hasOversizedPackage, setHasOversizedPackage,
    packageDetails, setPackageDetails,
    uploadedFiles,
    liabilityWaiverAccepted, setLiabilityWaiverAccepted,
    packagingAddons, setPackagingAddons,
    isSubscription, subscriptionUsage,
    carriers, packageSizes,
    handlePackageDetailChange, handleFileUpload, handlePackagingChange,
    handleNextFromPackages,
    // Step 3
    agreementAccepted, setAgreementAccepted,
    breakdown, totalCost,
    duplicateWarning, setDuplicateWarning,
    // Shared
    errors, handleBackStep,
  };

  return (
    <div className="signup-page">
      <nav className="nav-header">
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png"
            alt="ReturnPal"
            className="logo-image"
            style={{ height: '50px', width: 'auto' }}
          />
        </div>
      </nav>

      <div className="signup-container">
        <div className="signup-content">
          <h1 className="signup-title">Schedule Your Pickup</h1>
          <p className="signup-subtitle">Choose your pickup details and preferences</p>

          <div className="important-notice">
            <AlertCircle size={20} />
            <span>All returns are made within 48 hours of pick up time</span>
          </div>

          {/* Subscription Status Banner */}
          {isSubscription && subscriptionUsage && subscriptionUsage.subscription_status === 'active' && (
            <div style={{
              marginTop: '1.5rem',
              marginBottom: '1.5rem',
              padding: '1.25rem',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '3px solid #f59e0b',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)'
            }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#92400e', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🎉</span> {getPlanLabel(subscriptionUsage.plan_type)} - Active Subscription
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.9rem', color: '#78350f' }}>
                <div>
                  <strong>Pickups This Month:</strong><br />
                  {subscriptionUsage.pickups_used_this_month} of {getPlanMaxPickups(subscriptionUsage.plan_type)} used
                  {subscriptionUsage.pickups_remaining > 0 && (
                    <span style={{ color: '#10b981', fontWeight: '600' }}> ({subscriptionUsage.pickups_remaining} remaining)</span>
                  )}
                </div>
                <div>
                  <strong>Packages This Month:</strong><br />
                  {subscriptionUsage.packages_used_this_month} of {getPlanMaxPackages(subscriptionUsage.plan_type)} used
                  {subscriptionUsage.packages_remaining > 0 && (
                    <span style={{ color: '#10b981', fontWeight: '600' }}> ({subscriptionUsage.packages_remaining} remaining)</span>
                  )}
                </div>
              </div>
              {subscriptionUsage.packages_remaining > 0 && (
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#92400e', fontStyle: 'italic', marginBottom: 0 }}>
                  💡 Your packages roll over within the month. Use all {getPlanMaxPackages(subscriptionUsage.plan_type)} packages before being charged $5 for additional packages.
                </p>
              )}
            </div>
          )}

          {/* Upsell banner — shown only to non-subscribers */}
          {!isSubscription && (
            <div
              data-testid="schedule-upsell-banner"
              style={{
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
                padding: '1.25rem',
                backgroundColor: '#ecfdf5',
                borderRadius: '8px',
                border: '2px solid #10b981',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div style={{ flex: '1 1 280px' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#065f46', margin: 0 }}>
                  Ready to subscribe for more pickups and returns?
                </h3>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#047857' }}>
                  Save up to 30% with a monthly subscription. Your account info is already saved — just pick a plan and pay.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => navigate('/signup?upgrade=1')}
                data-testid="schedule-upgrade-btn"
                style={{ backgroundColor: '#10b981', color: 'white', fontWeight: 600 }}
              >
                See plans
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="signup-form">
            {/* Wizard Progress Indicator (3-step) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              flexWrap: 'wrap',
            }} data-testid="wizard-progress">
              {[
                { num: 1, label: 'Pickup Details' },
                { num: 2, label: 'Packages' },
                { num: 3, label: 'Review' },
              ].map((s, i) => (
                <React.Fragment key={s.num}>
                  {i > 0 && (
                    <div style={{ flex: '0 1 40px', height: '2px', backgroundColor: currentStep >= s.num ? '#10b981' : '#d1d5db' }} />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '9999px',
                      backgroundColor: currentStep >= s.num ? '#10b981' : '#d1d5db',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}>
                      {currentStep > s.num ? '✓' : s.num}
                    </div>
                    <span style={{
                      fontWeight: currentStep === s.num ? 700 : 500,
                      color: currentStep === s.num ? '#065f46' : '#6b7280',
                      fontSize: '0.9rem',
                    }}>
                      {s.label}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {currentStep === 1 && <PickupDetailsStep {...stepCtx} />}
            {currentStep === 2 && <PackagesStep {...stepCtx} />}
            {currentStep === 3 && <ReviewStep {...stepCtx} />}

          </form>
        </div>
      </div>
    </div>
  );
};

export default SchedulePickup;
